import { Pipe, PipeTransform } from '@angular/core';
import { LocaleService } from '../services/locale.service';

@Pipe({
  name: 'cultural',
  standalone: true,
  pure: false
})
export class CulturalPipe implements PipeTransform {
  constructor(private localeService: LocaleService) {}

  transform(
    value: number | string | Date | null | undefined,
    digits: string = '1.1-2',
    useGrouping: boolean = true,
    dateOptions?: Intl.DateTimeFormatOptions
  ): string {
    if (value == null) return '';

    if (value instanceof Date || this.isDateString(value)) {
      const dateValue = value instanceof Date ? value : new Date(value);
      if (isNaN(dateValue.getTime())) return '';
      return this.localeService.formatDate(dateValue, dateOptions);
    }

    const numValue = this.toNumber(value);
    if (!Number.isFinite(numValue)) return '';

    const { minInt, minFrac, maxFrac } = this.parseDigits(digits);
    const locale = this.resolveLocale(this.localeService.currentLocale);

    try {
      return new Intl.NumberFormat(locale, {
        minimumIntegerDigits: minInt,
        minimumFractionDigits: minFrac,
        maximumFractionDigits: maxFrac,
        useGrouping
      }).format(numValue);
    } catch {
      const isDa = (locale || '').toLowerCase().startsWith('da');
      const decimalChar = isDa ? ',' : '.';
      const thousandChar = isDa ? '.' : ',';

      const fixed = numValue.toFixed(maxFrac);
      let [intPart, fracPart = ''] = fixed.split('.');

      const intWithGroup = useGrouping
        ? intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandChar)
        : intPart;

      if (maxFrac > minFrac) {
        const trimmed = fracPart.replace(/0+$/, '');
        if (trimmed.length >= minFrac) fracPart = trimmed;
      }

      return fracPart ? `${intWithGroup}${decimalChar}${fracPart}` : intWithGroup;
    }
  }

  private resolveLocale(l?: string): string {
    const lc = (l || '').toLowerCase();
    if (lc.startsWith('da')) return 'da-DK';
    if (lc.startsWith('en')) return 'en-US';
    return 'en-US';
  }

  private parseDigits(d: string) {
    const m = /^(\d+)?\.(\d+)(?:-(\d+))?$/.exec((d || '').trim());
    const minInt = m?.[1] ? parseInt(m[1], 10) : 1;
    const minFrac = m?.[2] ? parseInt(m[2], 10) : 0;
    const maxFrac = m?.[3] ? parseInt(m[3], 10) : minFrac;
    return { minInt, minFrac, maxFrac };
  }

  private toNumber(value: number | string): number {
    if (typeof value === 'number') return value;

    let s = value.trim().replace(/[\u00A0\u2009\u202F\s']/g, '');

    const hasComma = s.includes(',');
    const hasDot = s.includes('.');

    if (hasComma && hasDot) {
      const lastComma = s.lastIndexOf(',');
      const lastDot = s.lastIndexOf('.');
      if (lastComma > lastDot) {
        s = s.replace(/\./g, '');
        s = s.replace(',', '.');
      } else {
        s = s.replace(/,/g, '');
      }
    } else if (hasComma && !hasDot) {
      s = s.replace(',', '.');
    }

    const n = parseFloat(s);
    return Number.isFinite(n) ? n : NaN;
  }

  private isDateString(value: any): boolean {
    if (typeof value !== 'string') return false;
    return /^\d{4}-\d{2}-\d{2}/.test(value) || /^\d{1,2}\/\d{1,2}\/\d{4}/.test(value);
  }
}
